/**
 * CompactSearchBar Component
 * A horizontal search bar displayed on all pages except homepage
 * Styled to match the Freestays blue theme
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';
import { Search, MapPin, CalendarIcon, Users, ChevronDown, Navigation, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CountrySelector, getCustomerCountry } from '@/components/CountrySelector';
import { DateRangeSheet } from '@/components/DateRangeSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CompactSearchBar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();

  // Search state (initialise from URL so the bar reflects the active search)
  const [destination, setDestination] = useState(() => urlSearchParams.get('destination') || '');
  const [destinationId, setDestinationId] = useState(() => urlSearchParams.get('destinationId') || '');
  const [checkIn, setCheckIn] = useState(() => {
    const v = urlSearchParams.get('checkIn');
    return v ? new Date(v) : null;
  });
  const [checkOut, setCheckOut] = useState(() => {
    const v = urlSearchParams.get('checkOut');
    return v ? new Date(v) : null;
  });
  const [adults, setAdults] = useState(() => parseInt(urlSearchParams.get('adults') || '2'));
  const [children, setChildren] = useState(() => parseInt(urlSearchParams.get('children') || '0'));
  const [showGuestPopover, setShowGuestPopover] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const searchContainerRef = useRef(null);

  // Re-sync state when URL params change (e.g. user navigates between pages)
  useEffect(() => {
    setDestination(urlSearchParams.get('destination') || '');
    setDestinationId(urlSearchParams.get('destinationId') || '');
    const ci = urlSearchParams.get('checkIn');
    const co = urlSearchParams.get('checkOut');
    setCheckIn(ci ? new Date(ci) : null);
    setCheckOut(co ? new Date(co) : null);
    setAdults(parseInt(urlSearchParams.get('adults') || '2'));
    setChildren(parseInt(urlSearchParams.get('children') || '0'));
  }, [urlSearchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Detect "pending changes" — local state differs from URL params.
  // Search/Update button only renders when there is something to confirm.
  const hasPendingChanges = useMemo(() => {
    try {
      const urlDest = urlSearchParams.get('destination') || '';
      const urlDestId = urlSearchParams.get('destinationId') || '';
      const urlCheckIn = urlSearchParams.get('checkIn') || '';
      const urlCheckOut = urlSearchParams.get('checkOut') || '';
      const urlAdults = urlSearchParams.get('adults') || '2';
      const urlChildren = urlSearchParams.get('children') || '0';
      if (destination !== urlDest) return true;
      if ((destinationId || '') !== urlDestId) return true;
      const ciStr = checkIn ? format(checkIn, 'yyyy-MM-dd') : '';
      const coStr = checkOut ? format(checkOut, 'yyyy-MM-dd') : '';
      if (ciStr !== urlCheckIn) return true;
      if (coStr !== urlCheckOut) return true;
      if (String(adults) !== urlAdults) return true;
      if (String(children) !== urlChildren) return true;
      return false;
    } catch (_) {
      return false;
    }
  }, [urlSearchParams, destination, destinationId, checkIn, checkOut, adults, children]);

  const [pulseUpdate, setPulseUpdate] = useState(false);
  useEffect(() => {
    if (hasPendingChanges) {
      setPulseUpdate(true);
      const tm = setTimeout(() => setPulseUpdate(false), 2000);
      return () => clearTimeout(tm);
    }
    setPulseUpdate(false);
  }, [hasPendingChanges]);

  const handleNearby = () => {
    if (!navigator.geolocation) return;
    setNearbyLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(`${API}/destinations/nearby?lat=${latitude}&lng=${longitude}`);
          const data = await response.json();
          if (data.destinations?.length > 0) {
            // Pick best destination: closest with >= 3 hotels, or most hotels
            const dests = data.destinations;
            const dest = dests.find(d => (d.hotel_count || 0) >= 3) || 
                         [...dests].sort((a, b) => (b.hotel_count || 0) - (a.hotel_count || 0))[0];
            selectDestination(dest);
            // Auto-navigate to search with nearby map view
            const _nbIn = checkIn || addDays(new Date(), 1);
            const _nbOut = checkOut || addDays(new Date(), 4);
            const params = new URLSearchParams({
              destination: [dest.name, dest.city, dest.country].filter(Boolean).join(', '),
              destinationId: dest.id || '',
              checkIn: format(_nbIn, 'yyyy-MM-dd'),
              checkOut: format(_nbOut, 'yyyy-MM-dd'),
              adults: adults.toString(),
              children: children.toString(),
              rooms: '1',
              nearby: 'true',
              userLat: latitude.toString(),
              userLng: longitude.toString()
            });
            navigate(`/search?${params.toString()}`);
          }
        } catch (err) {
          console.error('Nearby search error:', err);
        }
        setNearbyLoading(false);
        setShowSuggestions(false);
      },
      () => setNearbyLoading(false),
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  // Fetch destination suggestions (same API as homepage)
  const fetchSuggestions = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await fetch(`${API}/destinations/search?q=${encodeURIComponent(searchQuery)}&lang=${i18n.language || 'en'}`);
      const data = await response.json();
      setSuggestions(data.destinations || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleDestinationChange = (e) => {
    const value = e.target.value;
    setDestination(value);
    fetchSuggestions(value);
  };

  const selectDestination = (dest) => {
    const parts = [dest.name];
    if (dest.city) parts.push(dest.city);
    if (dest.country) parts.push(dest.country);
    setDestination(parts.join(', '));
    setDestinationId(dest.id);  // API returns 'id', not 'destination_id'
    setShowSuggestions(false);
  };

  const handleSearch = () => {
    if (!checkIn || !checkOut) {
      // No date range selected yet — fallback to next-day + 3 nights
      const fallbackIn = addDays(new Date(), 1);
      const fallbackOut = addDays(new Date(), 4);
      const params = new URLSearchParams({
        destination: destination,
        destinationId: destinationId || '',
        checkIn: format(checkIn || fallbackIn, 'yyyy-MM-dd'),
        checkOut: format(checkOut || fallbackOut, 'yyyy-MM-dd'),
        adults: adults.toString(),
        children: children.toString(),
        rooms: '1',
        customerCountry: getCustomerCountry()
      });
      navigate(`/search?${params.toString()}`);
      return;
    }
    const params = new URLSearchParams({
      destination: destination,
      destinationId: destinationId || '',
      checkIn: format(checkIn, 'yyyy-MM-dd'),
      checkOut: format(checkOut, 'yyyy-MM-dd'),
      adults: adults.toString(),
      children: children.toString(),
      rooms: '1',
      customerCountry: getCustomerCountry()
    });
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="bg-[#081D33]/5 dark:bg-[#081D33] border-b border-[#00A9E0]/10" data-testid="compact-search-bar" ref={searchContainerRef}>
      <div className="max-w-[1760px] mx-auto px-4 py-3">
        {/* Mobile Layout - Stacked vertically with destination prominent */}
        <div className="flex flex-col gap-3 md:hidden">
          {/* Destination Input - Full width on mobile - MUST BE FIRST */}
          <div className="relative w-full bg-card rounded-full shadow-sm border border-[#00A9E0]/20 dark:border-white/20">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00A9E0]" />
            <Input
              placeholder={t('search.whereTo', 'Where to?')}
              value={destination}
              onChange={handleDestinationChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="pl-9 h-12 bg-transparent text-foreground placeholder:text-muted-foreground border-0 focus:ring-0 rounded-full text-sm w-full"
              data-testid="compact-destination-input-mobile"
            />
            {/* Mobile Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover text-popover-foreground rounded-xl shadow-lg border border-border z-50 max-h-60 overflow-y-auto">
                {destination.length < 2 && (
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNearby}
                    className="w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center gap-3 text-sm text-foreground"
                    data-testid="compact-nearby-btn-mobile"
                  >
                    <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      {nearbyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" /> : <Navigation className="w-3.5 h-3.5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="font-medium">{t('search.nearMe', 'Near me')}</p>
                      <p className="text-xs text-muted-foreground">{t('search.nearMeDesc', 'Search hotels near your location')}</p>
                    </div>
                  </button>
                )}
                {suggestions.map((dest) => (
                  <button
                    key={dest.id}
                    onClick={() => selectDestination(dest)}
                    className="w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center gap-2 text-sm"
                  >
                    <MapPin className="w-4 h-4 text-[#00A9E0]" />
                    <span>{dest.name}{dest.city ? `, ${dest.city}` : ''}{dest.country ? `, ${dest.country}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Date and Guest row */}
          <div className="flex items-center gap-2">
            {/* Date range (combined) */}
            <DateRangeSheet
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={({ from, to }) => {
                setCheckIn(from);
                setCheckOut(to);
              }}
              trigger={
                <Button
                  variant="outline"
                  className="h-10 px-2 bg-card text-foreground border-[#00A9E0]/20 dark:border-white/20 hover:border-[#00A9E0] rounded-full text-xs flex-[2]"
                  data-testid="compact-daterange-btn-mobile"
                >
                  <CalendarIcon className="w-3 h-3 mr-1 text-[#00A9E0]" />
                  <span className="truncate">
                    {checkIn && checkOut
                      ? `${format(checkIn, 'MMM d')} — ${format(checkOut, 'MMM d')}`
                      : t('search.selectDates', 'Select dates')}
                  </span>
                </Button>
              }
            />

            {/* Guests */}
            <Popover open={showGuestPopover} onOpenChange={setShowGuestPopover}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-10 px-2 bg-card text-foreground border-[#00A9E0]/20 dark:border-white/20 hover:border-[#00A9E0] rounded-full text-xs flex-1"
                >
                  <Users className="w-3 h-3 mr-1 text-[#00A9E0]" />
                  <span className="truncate">{adults}A, {children}C</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-4 rounded-xl z-50" align="center">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('search.adults', 'Adults')}</span>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        onClick={(e) => { e.stopPropagation(); setAdults(Math.max(1, adults - 1)); }}
                        data-testid="compact-adults-minus"
                      >
                        -
                      </Button>
                      <span className="w-6 text-center font-medium" data-testid="compact-adults-count">{adults}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        onClick={(e) => { e.stopPropagation(); setAdults(Math.min(10, adults + 1)); }}
                        data-testid="compact-adults-plus"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('search.children', 'Children')}</span>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        onClick={(e) => { e.stopPropagation(); setChildren(Math.max(0, children - 1)); }}
                        data-testid="compact-children-minus"
                      >
                        -
                      </Button>
                      <span className="w-6 text-center font-medium" data-testid="compact-children-count">{children}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-8 h-8 p-0 rounded-full"
                        onClick={(e) => { e.stopPropagation(); setChildren(Math.min(6, children + 1)); }}
                        data-testid="compact-children-plus"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                    onClick={() => setShowGuestPopover(false)}
                    data-testid="compact-guests-done"
                  >
                    {t('common.done', 'Done')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Country Flag Selector */}
            <CountrySelector compact />
            
            {/* Search/Update Button — only when there are pending changes */}
            {hasPendingChanges && (
              <Button 
                onClick={handleSearch}
                className={`h-10 px-4 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold shadow-md ${pulseUpdate ? 'animate-pulse ring-2 ring-primary' : ''}`}
                title={t('search.confirmChanges', 'Confirm changes')}
                data-testid="compact-search-btn-mobile"
              >
                <Search className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Desktop Layout - Horizontal row */}
        <div className="hidden md:flex items-center gap-3">
          {/* Destination Input */}
          <div className="relative flex-1 min-w-[180px]">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00A9E0]" />
            <Input
              placeholder={t('search.whereTo', 'Where to?')}
              value={destination}
              onChange={handleDestinationChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="pl-9 h-10 bg-card text-foreground placeholder:text-muted-foreground border-[#00A9E0]/20 dark:border-white/20 focus:border-primary rounded-full text-sm"
              data-testid="compact-destination-input"
            />
            {/* Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover text-popover-foreground rounded-xl shadow-lg border border-border z-50 max-h-60 overflow-y-auto">
                {destination.length < 2 && (
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleNearby}
                    className="w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center gap-3 text-sm dark:text-white"
                    data-testid="compact-nearby-btn"
                  >
                    <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      {nearbyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" /> : <Navigation className="w-3.5 h-3.5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="font-medium">{t('search.nearMe', 'Near me')}</p>
                      <p className="text-xs text-muted-foreground">{t('search.nearMeDesc', 'Search hotels near your location')}</p>
                    </div>
                  </button>
                )}
                {suggestions.map((dest) => (
                  <button
                    key={dest.id}
                    onClick={() => selectDestination(dest)}
                    className="w-full px-4 py-2 text-left hover:bg-primary/5 flex items-center gap-2 text-sm dark:text-white"
                  >
                    <MapPin className="w-4 h-4 text-[#00A9E0]" />
                    <span>{dest.name}{dest.city ? `, ${dest.city}` : ''}{dest.country ? `, ${dest.country}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Range (combined check-in + check-out) */}
          <DateRangeSheet
            checkIn={checkIn}
            checkOut={checkOut}
            onChange={({ from, to }) => {
              setCheckIn(from);
              setCheckOut(to);
            }}
            trigger={
              <Button
                variant="outline"
                className="h-10 px-3 bg-card text-foreground border-[#00A9E0]/20 dark:border-white/20 hover:border-[#00A9E0] rounded-full text-sm min-w-[220px] justify-start"
                data-testid="compact-daterange-btn"
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-[#00A9E0]" />
                <span className="truncate">
                  {checkIn && checkOut
                    ? `${format(checkIn, 'MMM d')} — ${format(checkOut, 'MMM d')}`
                    : t('search.selectDates', 'Select dates')}
                </span>
              </Button>
            }
          />

          {/* Guests */}
          <Popover open={showGuestPopover} onOpenChange={setShowGuestPopover}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="h-10 px-3 bg-card text-foreground border-[#00A9E0]/20 dark:border-white/20 hover:border-[#00A9E0] rounded-full text-sm min-w-[140px] justify-start"
                data-testid="compact-guests-btn"
              >
                <Users className="w-4 h-4 mr-2 text-[#00A9E0]" />
                <span className="truncate">{adults} {t('search.adults', 'Adults')}, {children} {t('search.children', 'Children')}</span>
                <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4 rounded-xl z-50" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('search.adults', 'Adults')}</span>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 rounded-full"
                      onClick={(e) => { e.stopPropagation(); setAdults(Math.max(1, adults - 1)); }}
                    >
                      -
                    </Button>
                    <span className="w-6 text-center font-medium">{adults}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 rounded-full"
                      onClick={(e) => { e.stopPropagation(); setAdults(Math.min(10, adults + 1)); }}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('search.children', 'Children')}</span>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 rounded-full"
                      onClick={(e) => { e.stopPropagation(); setChildren(Math.max(0, children - 1)); }}
                    >
                      -
                    </Button>
                    <span className="w-6 text-center font-medium">{children}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 rounded-full"
                      onClick={(e) => { e.stopPropagation(); setChildren(Math.min(6, children + 1)); }}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <Button
                  className="w-full rounded-full bg-primary hover:bg-primary/90"
                  onClick={() => setShowGuestPopover(false)}
                >
                  {t('common.done', 'Done')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Country Flag Selector */}
          <CountrySelector compact />

          {/* Search/Update Button — only when there are pending changes */}
          {hasPendingChanges && (
            <Button 
              onClick={handleSearch}
              className={`h-10 px-6 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold shadow-md hover:shadow-lg transition-all ${pulseUpdate ? 'animate-pulse ring-2 ring-primary' : ''}`}
              data-testid="compact-search-btn"
              title={t('search.confirmChanges', 'Confirm changes')}
            >
              <Search className="w-4 h-4 mr-2" />
              {t('search.updateSearch', 'Update')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactSearchBar;
